import React, { useEffect, useMemo } from 'react'
import { UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, type RadioOption } from '@node-space/ui-components'
import {
  type BeneficiaryTransferDestination,
  BeneficiaryTransferDestination as TransferDestination,
  BeneficiaryDetailsCurrency,
  PaymentMethods,
} from 'types/beneficiaries'
import {
  SEPA_METHODS,
  SEPA_CREDIT_INSTANT_TRANSFER_LIMIT
} from '../../constants'
import { FiatSendFormData } from '../../form/fiatSendFormSchema'
import { getAvailablePaymentMethods } from '../../utils'
import { PAYMENT_METHODS_LABELS } from 'pages/Contacts/constants'
import { PaymentMethodGroup } from './PaymentMethodGroup'
import { AchSameDayCheckbox } from './AchSameDayCheckbox'

interface PaymentMethodSelectionProps {
  formValues: FiatSendFormData
  walletTransferDestinations: BeneficiaryTransferDestination[]
  bankAccountPaymentMethods: string[]
  currency: string
  amount?: string
  error?: string
  register: UseFormRegister<FiatSendFormData>
  setValue: UseFormSetValue<FiatSendFormData>
}

export const PaymentMethodSelection = ({
  formValues,
  walletTransferDestinations,
  bankAccountPaymentMethods,
  currency,
  amount,
  error,
  register,
  setValue,
}: PaymentMethodSelectionProps) => {
  const { t } = useTranslation()

  const availablePaymentMethods = useMemo(
    () =>
      getAvailablePaymentMethods(currency, walletTransferDestinations, bankAccountPaymentMethods),
    [walletTransferDestinations, bankAccountPaymentMethods, currency]
  )

  const paymentMethodOptions: RadioOption[] = useMemo(() => {
    switch (currency) {
      case BeneficiaryDetailsCurrency.EUR: {
        const hasSepa = availablePaymentMethods.some(method => SEPA_METHODS.has(method))
        const hasSwift = availablePaymentMethods.includes(PaymentMethods.SWIFT)
        if (hasSepa) {
          return [
            {
              label: `${t('wallets.fiatSend.autoPaymentMethod')} (${t('wallets.fiatSend.recommended')})`,
              value: '',
              secondaryLabel: t('wallets.fiatSend.autoPaymentMethodDescription'),
            },
            {
              label: t('wallets.fiatSend.sepaInstant'),
              value: PaymentMethods.SEPA_INSTANT,
              secondaryLabel: t('wallets.fiatSend.sepaInstantDescription'),
              disabled: Number(amount) > SEPA_CREDIT_INSTANT_TRANSFER_LIMIT,
            },
            {
              label: t('wallets.fiatSend.sepaCredit'),
              value: PaymentMethods.SEPA,
              secondaryLabel: t('wallets.fiatSend.sepaCreditDescription'),
            },
          ]
        }

        if (hasSwift) {
          return [
            {
              label: t('paymentMethodsLabels.swift'),
              value: PaymentMethods.SWIFT,
            },
          ]
        }
        
        return []
      }
      case BeneficiaryDetailsCurrency.USD:
        return availablePaymentMethods.map(method => ({
          label: t(PAYMENT_METHODS_LABELS[method] ?? method),
          value: method,
        }))
      case BeneficiaryDetailsCurrency.GBP:
      default:
        return []
    }
  }, [availablePaymentMethods, amount, currency, t, walletTransferDestinations])

  useEffect(() => {
    const isSWIFT = walletTransferDestinations?.includes(TransferDestination.INTERNATIONAL)
    const isEUR = currency === BeneficiaryDetailsCurrency.EUR

    if (isSWIFT) {
      setValue('paymentMethod', PaymentMethods.SWIFT)
      return
    } 

    const isSingleOption = paymentMethodOptions?.length === 1
    const isPaymentMethodSet = !!formValues?.paymentMethod

    if (isSingleOption || (isPaymentMethodSet && !isEUR)) {
      setValue('paymentMethod', paymentMethodOptions?.[0].value ?? '')
    }
  }, [paymentMethodOptions, currency, formValues?.paymentMethod, walletTransferDestinations, setValue])

  if (!paymentMethodOptions.length) return null

  return (
    <>
      {paymentMethodOptions?.length > 1 && (
        
        
        <PaymentMethodGroup formValues={formValues} paymentMethodOptions={paymentMethodOptions} error={error} setValue={setValue} />

      )}

      {formValues?.paymentMethod === PaymentMethods.ACH && (
        <Box paddingT={12} paddingB={8}>
          <AchSameDayCheckbox formValues={formValues} register={register} setValue={setValue} />
        </Box>
      )}
    </>
  )
}
